import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "@i18n";
import { CircularProgress, MenuItem, Select } from "@mui/material";
import _ from "lodash";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { jiraTicketsUrl, jiraController, fetchIsWhiteListed } from "../api/jira.api";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { convertDate } from "@Services/luxonHelper";
import { exportPdf, EXPORT_TYPE } from "@Services/fileDownload";
import { convertHtmlToAdf, getReportedFilter, mutedStatusColors } from "./constants";
import { getJiraStatusHex, jiraIdMapper, jiraStatusLabelToId } from "@Helpers";
import { JIRA_CREATED_BY_FILTERS } from "@Helpers/jiraConstants";
import ChangeTicketTypeModal, { ChangeTypePayload } from "./ChangeTicketTypeModal";
import { slaReportPortalOptions } from "@Features/sla/slaReportModel";
import SlaExportModal, { type SlaExportSelection } from "@Features/sla/SlaExportModal";
import { formatDuration } from "@Features/sla/useSlaCountdown";
import IconComponent from "@CubeComponents/icons";
import SplitButton, { type SplitButtonOption } from "@CubeComponents/html/SplitButtonComponent";
import { getAllNotificationPageUrl } from "@Services/utils/helpers";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { GetUsersForTicketCreationResponse, UserForTicketCreation } from "@ServerTypes/jira/jira.schema";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import useJiraTickets from "./useJiraTickets";
import Text from "@CubeComponents/html/Text/Text";
import Box from "@mui/material/Box";
import ToolTipComponent from "@CubeComponents/html/tooltip";

import {
  CreateOnBehalfComponent,
  SelectUserComponent,
  SelectPortalComponent,
  SummaryComponent,
  DescriptionComponent,
  AttachmentComponent,
  PriorityComponent,
  ImpactComponent,
  SeverityComponent,
  LinkUrlComponent,
  VisibilityComponent,
} from "./JiraTicketFields";
import type { ReactNode } from "react";
import type { SelectChangeEvent } from "@mui/material";
import type { TranslatedString } from "@i18n";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { Sla, SlaClock } from "@ServerTypes/sla/sla.schema";
import type { SlaReportTicket } from "@Features/sla/slaReportModel";
import type {
  ColumnDefinition,
  GridClickEvent,
  GridRowBase,
  LayoutData,
  QueryShape,
  SlickGrid1Ref,
  SlickGridProps,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";

type JiraTicketRow = GridRowBase &
  SlaReportTicket & {
    source?: string;
    organization?: { name?: string };
    portal?: { frontendUrl?: string };
    createdBy?: { _id?: ObjectIdString; fullName?: string; email?: string };
    cubeDevTicketKey?: string;
    cubeDevTicketStatus?: { name?: string; categoryKey?: string };
    lastStatusChangeAt?: number;
    createdOn: number;
    fields: {
      summary?: string;
      issueType: { id?: string; label?: string };
      issueStatus: { id?: string; label?: string };
      issueStatusCategory?: { id?: string; label?: string };
      priority?: { id?: string; label?: string };
      sla?: Sla;
    };
  };

/**
 * `ColumnDefinition` keys a column's `field` off `keyof` the row type, but this grid's fields are the
 * dotted DB paths ("fields.issueType.label") — the form the server's `JIRA_TICKETS_FILTERABLE_FIELDS`
 * allow-list expects the grid to send. Binding the column to an index-signature view of the row is
 * what admits them; the row type itself stays free of one, so `_.get` still resolves its real paths.
 */
type JiraTicketColumn = ColumnDefinition<JiraTicketRow & Record<string, unknown>, string>;

/** The argument SlickGrid1 hands `onSelectedRowIdsChanged`; its own event-bag type is not exported. */
type SelectedRowIdsEvent = Parameters<NonNullable<SlickGridProps<JiraTicketRow>["onSelectedRowIdsChanged"]>>[0];

/**
 * One entry of `useJiraTickets`' `ticketCreationFields.fields` — a heterogeneous literal array whose
 * entries carry a heading, an optional sub-heading and the caller-supplied component, each gated by
 * the ticket types it may appear in. `allowIn` is declared to include the form state's own
 * `null | undefined`, so `.includes(showTicketCreationForm.type)` keeps returning false for an unset
 * type rather than needing the value asserted.
 */
type TicketFormField = {
  heading: { text: TranslatedString; allowIn: (string | null | undefined)[] };
  subHeading?: { text: TranslatedString; allowIn: (string | null | undefined)[] };
  component: { component: ReactNode; allowIn: (string | null | undefined)[] };
};

const JiraTickets = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const portalData = useSelector((state: RootState) => state.portalData);
  const portalSettingInfo = useSelector((state: RootState) => state.portalSettingInfo);
  const customSupportSlaEnabled = Boolean(portalData?.customSupportSla);

  const [searchParams] = useSearchParams();
  const section = searchParams.get("supportType")?.toUpperCase() || null;
  const createdBy = searchParams.get("createdBy") || "createdByMe";
  const referrerUrl = searchParams.get("referrerUrl") || "";

  const dataGrid = useRef<SlickGrid1Ref<JiraTicketRow> | null>(null);

  const [columnDefinitions, setColumnDefinitions] = useState<JiraTicketColumn[]>([]);
  const [isWhiteListedUser, setIsWhiteListedUser] = useState<boolean | null>(null);
  const [allUsersForTicketCreation, setAllUsersForTicketCreation] = useState<UserForTicketCreation[]>([]);
  const [createdByFilter, setCreatedByFilter] = useState<string>(createdBy);
  const [query, setQuery] = useState<QueryShape>({
    action: "getList",
    createdBy,
    requiresCounts: true,
  });
  const [viewTrigger, setViewTrigger] = useState<Record<string, unknown> | false>(false);
  const [selectedTicket, setSelectedTicket] = useState<JiraTicketRow | null>(null);
  const [showChangeTypeModal, setShowChangeTypeModal] = useState<boolean>(false);
  const [changeTypeLoading, setChangeTypeLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [showSlaExportModal, setShowSlaExportModal] = useState<boolean>(false);
  const exportingRef = useRef<boolean>(false);

  const {
    ticketCreationFields,
    isCurrentUserWhiteListed,
    setIsCurrentUserWhiteListed,
    showTicketCreationForm,
    setShowTicketCreationForm,
    handleSaveBugDetails,
    showCreationLoader,
    closeTicketCreationModal,
    isDisabledSaveButton,
    ticketHeaderInfo,
    createTicketMenuItems,
  } = useJiraTickets({
    allUsers: allUsersForTicketCreation,
    convertHtmlToAdf,
    referrerUrl,
    section,
    onSaveSucessfull: () => {
      setTimeout(() => {
        refreshGrid();
      }, 1000);
    },
    closeTicketCreationModalCallback: (section: string | undefined) => {
      if (section) {
        navigate(getAllNotificationPageUrl({ section: "support" }));
      }
    },
    component: {
      createOnBehalfComopnent: CreateOnBehalfComponent,
      summaryComponent: SummaryComponent,
      descriptionComponent: DescriptionComponent,
      priorityComponent: PriorityComponent,
      impactComponent: ImpactComponent,
      severityComponent: SeverityComponent,
      linkUrlComponent: LinkUrlComponent,
      visibilityComponent: VisibilityComponent,
      selectUserComponent: SelectUserComponent,
      selectPortalComponent: SelectPortalComponent,
      attachmentComponent: AttachmentComponent,
    },
  });

  const fetchUsersForTicketCreation = useCallback(async () => {
    const response = await axiosWrapper.get<GetUsersForTicketCreationResponse>(
      `${getBackendUrl()}/jira/getUsersForTicketCreation`,
    );

    if (response?.data?.status) {
      const filteredUsers = response.data.result?.filter((user) => user._id !== userDetails._id) ?? [];
      setAllUsersForTicketCreation(filteredUsers);
    }
  }, [userDetails._id]);

  const loadIsWhiteListed = async () => {
    setIsWhiteListedUser(await fetchIsWhiteListed());
  };

  useEffect(() => {
    loadIsWhiteListed();
  }, []);

  const canViewSla = Boolean(isWhiteListedUser) || Boolean(portalSettingInfo?.viewSupportSla?.enable);

  useEffect(() => {
    if (isWhiteListedUser !== null) {
      setIsCurrentUserWhiteListed(isWhiteListedUser);
    }
  }, [isWhiteListedUser, setIsCurrentUserWhiteListed]);

  useEffect(() => {
    if (isCurrentUserWhiteListed) {
      fetchUsersForTicketCreation();
    }
  }, [isCurrentUserWhiteListed, fetchUsersForTicketCreation]);

  useEffect(() => {
    if (!isWhiteListedUser) return;
    const id = setInterval(() => {
      const grid = dataGrid.current?.slickGrid;
      grid?.invalidate();
      grid?.render();
    }, 60_000);
    return () => clearInterval(id);
  }, [isWhiteListedUser]);

  useEffect(() => {
    if (!Object.keys(userDetails).length || isWhiteListedUser === null) return;

    const CLOSED_STATUS_ID = jiraStatusLabelToId["Closed"];
    const timeSinceStatusChangeLabel = (row: JiraTicketRow): string =>
      _.get(row, "fields.issueStatus.id") === CLOSED_STATUS_ID
        ? "-"
        : formatDuration(Date.now() - (row.lastStatusChangeAt ?? row.createdOn));
    const devTicketLabel = (row: JiraTicketRow): string => {
      if (!row.cubeDevTicketKey) return "-";
      return row.cubeDevTicketStatus?.name
        ? `${row.cubeDevTicketKey} - ${row.cubeDevTicketStatus.name}`
        : row.cubeDevTicketKey;
    };

    const isBugRow = (row: JiraTicketRow) => _.get(row, "fields.issueType.id") === jiraIdMapper["Report a Bug"];
    const priorityLabel = (row: JiraTicketRow) => (isBugRow(row) ? _.get(row, "fields.priority.label") : "");
    const slaClock = (row: JiraTicketRow, variant: "response" | "resolution"): SlaClock | undefined =>
      isBugRow(row) ? (_.get(row, `fields.sla.${variant}`) as SlaClock | undefined) : undefined;
    const slaInstant = (ms: number | null | undefined) => (ms ? String(convertDate(ms, "dateForPdfExport")) : "-");
    const slaMet = (row: JiraTicketRow, variant: "response" | "resolution") => {
      const clock = slaClock(row, variant);
      if (!clock) return "-";
      if (clock.actualAt != null) return clock.metInTime ? String(t("Yes")) : String(t("No"));
      if (clock.state === "breachedOpen") return String(t("Breached"));
      return String(t("Open"));
    };
    const slaColumnCommon = {
      sortable: false,
      disableSorting: true,
      disableFiltering: true,
      disableGrouping: true,
      initiallyHidden: true,
    };
    const slaColumns: JiraTicketColumn[] = [
      ...(customSupportSlaEnabled
        ? []
        : [
            {
              ...slaColumnCommon,
              id: "priority",
              field: "fields.priority.label",
              name: t("Priority"),
              minWidth: 110,
              formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
                priorityLabel(value) || "-",
              _valueGetter: (args: JiraTicketRow) => priorityLabel(args) || "",
              _excelValueGetter: (row: JiraTicketRow) => priorityLabel(row) || "-",
            },
          ]),
      {
        ...slaColumnCommon,
        id: "firstResponseDeadline",
        field: "fields.sla.response.deadlineAt",
        name: t("First Response SLA Deadline"),
        minWidth: 200,
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          slaInstant(slaClock(value, "response")?.deadlineAt),
        _valueGetter: (args: JiraTicketRow) => slaClock(args, "response")?.deadlineAt ?? null,
        _excelValueGetter: (row: JiraTicketRow) => slaInstant(slaClock(row, "response")?.deadlineAt),
      },
      {
        ...slaColumnCommon,
        id: "actualResponse",
        field: "fields.sla.response.actualAt",
        name: t("Actual Response"),
        minWidth: 180,
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          slaInstant(slaClock(value, "response")?.actualAt),
        _valueGetter: (args: JiraTicketRow) => slaClock(args, "response")?.actualAt ?? null,
        _excelValueGetter: (row: JiraTicketRow) => slaInstant(slaClock(row, "response")?.actualAt),
      },
      {
        ...slaColumnCommon,
        id: "responseMet",
        field: "fields.sla.response.metInTime",
        name: t("Response Met"),
        minWidth: 130,
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          slaMet(value, "response"),
        _valueGetter: (args: JiraTicketRow) => slaMet(args, "response"),
        _excelValueGetter: (row: JiraTicketRow) => slaMet(row, "response"),
      },
      {
        ...slaColumnCommon,
        id: "resolutionDeadline",
        field: "fields.sla.resolution.deadlineAt",
        name: t("Resolution SLA Deadline"),
        minWidth: 200,
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          slaInstant(slaClock(value, "resolution")?.deadlineAt),
        _valueGetter: (args: JiraTicketRow) => slaClock(args, "resolution")?.deadlineAt ?? null,
        _excelValueGetter: (row: JiraTicketRow) => slaInstant(slaClock(row, "resolution")?.deadlineAt),
      },
      {
        ...slaColumnCommon,
        id: "actualResolution",
        field: "fields.sla.resolution.actualAt",
        name: t("Actual Resolution"),
        minWidth: 180,
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          slaInstant(slaClock(value, "resolution")?.actualAt),
        _valueGetter: (args: JiraTicketRow) => slaClock(args, "resolution")?.actualAt ?? null,
        _excelValueGetter: (row: JiraTicketRow) => slaInstant(slaClock(row, "resolution")?.actualAt),
      },
      {
        ...slaColumnCommon,
        id: "resolutionMet",
        field: "fields.sla.resolution.metInTime",
        name: t("Resolution Met"),
        minWidth: 130,
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          slaMet(value, "resolution"),
        _valueGetter: (args: JiraTicketRow) => slaMet(args, "resolution"),
        _excelValueGetter: (row: JiraTicketRow) => slaMet(row, "resolution"),
      },
    ];

    const baseColumns: JiraTicketColumn[] = [
      {
        id: "type",
        field: "fields.issueType.label",
        name: t("Type"),
        minWidth: 150,
        filterType: "Text",
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) => typeTemplate(value),
        _valueGetter: (args) => _.get(args, "fields.issueType.label"),
      },
      {
        id: "jiraTicketKey",
        field: "jiraTicketKey",
        name: t("ID"),
        filterType: "Text",
        minWidth: 100,
      },
      {
        id: "summary",
        field: "fields.summary",
        name: t("Summary"),
        filterType: "Text",
        minWidth: 250,
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          _.get(value, "fields.summary"),
        _valueGetter: (args) => _.get(args, "fields.summary"),
        wrapText: true,
      },
      ...(isWhiteListedUser
        ? ([
            {
              id: "portalName",
              field: "portal.name",
              name: t("Portal Name"),
              filterType: "Text",
              formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
                portalNameTemplate(value),
              minWidth: 250,
              _valueGetter: (args) => _.get(args, "portal.name"),
            },
            {
              id: "organizationName",
              field: "organization.name",
              name: t("Organization"),
              filterType: "Text",
              minWidth: 250,
              formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
                value?.organization?.name || "-",
              _valueGetter: (args) => _.get(args, "organization.name"),
            },
            {
              id: "source",
              field: "source",
              name: t("Source"),
              filterType: "Text",
              formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
                sourceTemplate(value),
              minWidth: 150,
            },
          ] as JiraTicketColumn[])
        : []),
      {
        id: "createdBy",
        field: "createdBy.fullName",
        name: t("Created By"),
        filterType: "Text",
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          createdByTemplate(value),
        _valueGetter: (args: JiraTicketRow) => _.get(args, "createdBy.fullName"),
        minWidth: 150,
      },
      {
        id: "createdOn",
        field: "createdOn",
        name: t("Created On"),
        filterType: "Date",
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          createdOnTemplate(value),
        _excelValueGetter: (row: JiraTicketRow) => String(convertDate(_.get(row, "createdOn"), "dateForPdfExport")),
        minWidth: 150,
      },
      {
        id: "status",
        field: "fields.issueStatus.label",
        name: t("Status"),
        filterType: "Text",
        minWidth: 200,
        formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
          statusTemplate(value),
        _valueGetter: (args) => _.get(args, "fields.issueStatus.label"),
      },
      ...(isWhiteListedUser
        ? ([
            {
              id: "timeSinceLastStatusChange",
              field: "lastStatusChangeAt",
              name: t("Time Since Last Status Change"),
              minWidth: 200,
              sortable: false,
              disableSorting: true,
              disableFiltering: true,
              disableGrouping: true,
              formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
                timeSinceStatusChangeLabel(value),
              _valueGetter: (args) => args.lastStatusChangeAt ?? args.createdOn,
              _excelValueGetter: (row: JiraTicketRow) => timeSinceStatusChangeLabel(row),
            },
            {
              id: "cubeDevTicketKey",
              field: "cubeDevTicketKey",
              name: t("Development Ticket"),
              minWidth: 220,
              sortable: false,
              disableSorting: true,
              disableFiltering: true,
              disableGrouping: true,
              formatter: (data: number, params: number, a: unknown, b: unknown, value: JiraTicketRow) =>
                devTicketTemplate(value),
              _valueGetter: (args) => args.cubeDevTicketKey ?? "",
              _excelValueGetter: (row: JiraTicketRow) => devTicketLabel(row),
            },
          ] as JiraTicketColumn[])
        : []),
      ...(canViewSla ? slaColumns : []),
    ];

    setColumnDefinitions(baseColumns);
  }, [userDetails, isWhiteListedUser, canViewSla, customSupportSlaEnabled]);

  useEffect(() => {
    if (portalData?._id && !createdByFilter) {
      setCreatedByFilter(createdBy);
    }
  }, [portalData?._id, createdByFilter, createdBy]);

  const typeTemplate = (args: JiraTicketRow) => {
    let imgSrc;
    if (args.fields.issueType.id === "10012") {
      imgSrc = "suggestAFeature";
    } else if (args.fields.issueType.id === "10013") {
      imgSrc = "bugReport";
    } else {
      imgSrc = "askAQuestion";
    }
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <Box className="flex gap-2 items-center">
        <IconComponent name={imgSrc} width="1.25rem" height="1.25rem" />
        <span>{args.fields.issueType.label}</span>
      </Box>,
    );
    return container;
  };

  const portalNameTemplate = (args: JiraTicketRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    const portalName = args?.portal?.name;
    const frontendUrl = args?.portal?.frontendUrl;
    const displayText = portalName && frontendUrl ? `${portalName} (${frontendUrl})` : "-";
    root.render(<Text>{displayText}</Text>);
    return container;
  };

  const statusTemplate = (value: JiraTicketRow) => {
    const { bg, border } = mutedStatusColors(
      getJiraStatusHex(_.get(value, "fields.issueStatus.id"), _.get(value, "fields.issueStatusCategory.id")),
    );
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <Box
        sx={{ backgroundColor: bg, color: "#192048", border: `1px solid ${border}` }}
        className="font-semibold px-4 py-2 rounded whitespace-nowrap w-fit items-center"
      >
        {value.fields.issueStatus.label}
      </Box>,
    );

    return container;
  };

  const createdByTemplate = (value: JiraTicketRow) => {
    const container = document.createElement("div");
    renderUserTag(container, {
      id: _.get(value, "createdBy._id"),
      fullName: _.get(value, "createdBy.fullName"),
      email: _.get(value, "createdBy.email"),
    });
    return container;
  };

  const createdOnTemplate = (value: JiraTicketRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <ToolTipComponent position="bottom" content={convertDate(_.get(value, "createdOn"), "dateForTooltip")}>
        {convertDate(_.get(value, "createdOn"), "dateForText")}
      </ToolTipComponent>,
    );
    return container;
  };

  const devTicketTemplate = (value: JiraTicketRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    if (!value.cubeDevTicketKey) {
      root.render(<Text>-</Text>);
      return container;
    }
    root.render(
      <Box className="flex gap-1 items-center">
        <a
          href={`https://cube-inc.atlassian.net/browse/${value.cubeDevTicketKey}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          {value.cubeDevTicketKey}
        </a>
        {value.cubeDevTicketStatus?.name && <span>{`- ${value.cubeDevTicketStatus.name}`}</span>}
      </Box>,
    );
    return container;
  };

  const sourceTemplate = (value: JiraTicketRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<Text>{value?.source}</Text>);
    return container;
  };

  const refreshGrid = useCallback(() => {
    setQuery({
      action: "getList",
      createdBy: createdByFilter,
      requiresCounts: true,
    });
  }, [createdByFilter]);

  const handleClickLayout = (params: LayoutData) => {
    const saved = (params?.queryConditions as { createdBy?: string } | null | undefined)?.createdBy;
    if (!saved || saved === createdByFilter) return;
    searchParams.set("createdBy", saved);
    setCreatedByFilter(saved);
    setQuery({ action: "getList", createdBy: saved, requiresCounts: true });
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };
  const canRaiseSupportTicket = isCurrentUserWhiteListed || !!portalSettingInfo?.raiseSupportTicket?.enable;

  const createTicketOptions: SplitButtonOption[] = createTicketMenuItems.map((item) => ({
    id: item.id,
    optionDisplay: (
      <Box className="flex gap-2 items-center">
        <IconComponent name={item.iconName} width="1.25rem" height="1.25rem" />
        <Text>{item.text}</Text>
      </Box>
    ),
    buttonDisplay: (
      <Box className="flex gap-2 items-center">
        <IconComponent name={item.iconName} width="1rem" height="1rem" />
        <Text>{item.text}</Text>
      </Box>
    ),
  }));

  const openTicketCreationForm = (option: SplitButtonOption) => {
    const id = typeof option === "string" ? option : option.id;
    if (!id) return;
    setShowTicketCreationForm({ type: String(id), status: true });
  };

  const createTicketDropdown = () => (
    <Box className="ms-4" data-testid="createBtn">
      <SplitButton
        options={createTicketOptions}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={!canRaiseSupportTicket}
        onClick={(_e, option) => openTicketCreationForm(option)}
        onChange={(_e, option) => openTicketCreationForm(option)}
      />
    </Box>
  );

  /** Scope is the modal's month (and portal); the grid's column filters are not carried over. */
  const handleExportSlaReport = async ({ month, portal }: SlaExportSelection) => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setIsExporting(true);
    try {
      // The widest scope the caller is entitled to, not the grid's "Created By" dropdown, which
      // would silently drop most of the month. Neither value grants anything new: the server
      // downgrades createdByAnyPortalUsers for non-whitelisted callers.
      const reportCreatedBy = isWhiteListedUser
        ? JIRA_CREATED_BY_FILTERS.CREATED_BY_ANY_PORTAL_USERS
        : JIRA_CREATED_BY_FILTERS.CREATED_BY_PORTAL_USERS;
      const portalFrontendUrl = portal?.frontendUrl ?? null;

      // No pre-flight query. An empty month is not an error — it is exported with that stated in the
      // PDF — and a ticket set spanning both SLA models is unreachable now that the export names one
      // portal, and a portal has one model.
      const result = await exportPdf({
        supportReport: true,
        supportCreatedBy: reportCreatedBy,
        supportMonth: month,
        ...(portalFrontendUrl ? { supportPortalUrl: portalFrontendUrl } : {}),
        exportType: EXPORT_TYPE.PDF,
      });
      if (result.status) setShowSlaExportModal(false);
    } catch {
      dispatch(showToastNotification({ content: t("Failed to export report"), type: TOAST_TYPE_ERROR }));
    } finally {
      exportingRef.current = false;
      setIsExporting(false);
    }
  };

  // The same list the create-on-behalf-of picker is built from, already fetched above —
  // deduplicated to one row per portal. Only whitelisted users have it, which is exactly who gets
  // the picker.
  const slaReportPortals = useMemo(
    () => slaReportPortalOptions(allUsersForTicketCreation),
    [allUsersForTicketCreation],
  );

  const exportSlaReportButton = () => (
    <Box className="ms-4" data-testid="exportSlaBtn">
      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={isExporting}
        onClick={() => setShowSlaExportModal(true)}
      >
        <span className="flex gap-2 items-center">
          <IconComponent name="PdfExport" width="1rem" height="1rem" />
          <Text>{t("Export SLA Report")}</Text>
        </span>
      </ButtonComponent>
    </Box>
  );

  const toolBar = [
    {
      id: "createTicket",
      template: () => createTicketDropdown(),
    },
    ...(isCurrentUserWhiteListed
      ? [
          {
            id: "changeType",
            template: () => (
              <Box className="ms-4" data-testid="changeTypeBtn">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  disabled={!selectedTicket}
                  onClick={openChangeTypeModal}
                >
                  <span className="flex gap-2 items-center">
                    <IconComponent name="ChangeTicketType" width="1rem" height="1rem" />
                    <Text>{t("Change Type")}</Text>
                  </span>
                </ButtonComponent>
              </Box>
            ),
          },
        ]
      : []),
    ...(canViewSla
      ? [
          {
            id: "exportSla",
            template: () => exportSlaReportButton(),
          },
        ]
      : []),
  ];

  const recordClick = (args: GridClickEvent) => {
    const rowIndex = args.detail.args.row;
    // KNOWN BUG, preserved verbatim: `dataGrid.current` is dereferenced with no guard here, though the
    // sibling selection handler checks it. Erased assertion; a guard would change behaviour.
    const clickedRow = dataGrid.current!.slickGrid.getDataItem(rowIndex);
    if (args.detail.args.cell != 0) {
      navigate(
        getAllNotificationPageUrl({
          section: "support",
          ticketId: clickedRow._id,
        }),
      );
    }
  };

  const handleSelectionChange = (args: SelectedRowIdsEvent) => {
    // SlickGrid1 declares this callback's argument as an untyped event bag; what it actually
    // dispatches is `{ detail: { args: { rows } } }`, named at this single read.
    const rows: number[] = (args as { detail?: { args?: { rows?: number[] } } })?.detail?.args?.rows ?? [];
    if (rows.length === 1) {
      const ticket = dataGrid.current?.slickGrid?.getDataItem(rows[0]!);
      setSelectedTicket(ticket ?? null);
    } else {
      setSelectedTicket(null);
    }
  };

  const openChangeTypeModal = () => {
    if (!selectedTicket) return;
    setShowChangeTypeModal(true);
  };

  const handleChangeTypeConfirm = async (payload: ChangeTypePayload) => {
    setChangeTypeLoading(true);
    try {
      // `statusResponse` is a discriminated union that only exposes `errorMessage` on its failure
      // branch, and the `?.` chain below reads it without narrowing to that branch. Named at the
      // boundary so the read stays as written.
      const res = (await jiraController({
        value: {
          action: "changeType",
          ticketId: payload.ticketId,
          newIssueType: payload.newIssueType,
          newStatus: payload.newStatus,
          ...(payload.impact ? { impact: payload.impact } : {}),
          ...(payload.severity ? { severity: payload.severity } : {}),
        },
      })) as Awaited<ReturnType<typeof jiraController>> & { data: { errorMessage?: string } };
      if (res?.data?.status) {
        dispatch(
          showToastNotification({
            content: t("Ticket type changed successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        setShowChangeTypeModal(false);
        setSelectedTicket(null);
        refreshGrid();
      } else {
        dispatch(
          showToastNotification({
            // KNOWN GAP, preserved verbatim: the raw server string is shown untranslated here.
            // Asserted rather than wrapped in `untranslated(...)`, because a call would emit.
            content: (res?.data?.errorMessage as TranslatedString) || t("Failed to change ticket type"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch {
      dispatch(
        showToastNotification({
          content: t("Failed to change ticket type"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } finally {
      setChangeTypeLoading(false);
    }
  };

  const getJiraGrid = () => (
    <Box className="px-4 pb-4 h-full">
      <SlickGrid1
        ref={dataGrid}
        // These columns are built as plain literals rather than through `typedColumns()`, whose brand
        // the prop asks for — calling it here would emit a real function call, so the brand is asserted
        // via an intersection that includes the real type.
        columnDefinitions={columnDefinitions as typeof columnDefinitions & ValidatedColumnDefinition<JiraTicketRow>[]}
        remoteDataSource={jiraTicketsUrl}
        query={query}
        onClick={recordClick}
        onSelectedRowIdsChanged={handleSelectionChange}
        toolbar={toolBar}
        excelExport
        showToolbarBorder
        enableSortingOption
        enableFilteringOption
        enableGroupingOption
        allowGrouping
        enableColumnChooser
        gridOptions={{ showPreHeaderPanel: true }}
        enableColumnPicker={false}
        triggerUpdateCurrentView={viewTrigger}
        enableViews={isCurrentUserWhiteListed}
        {...(isCurrentUserWhiteListed && {
          viewsOptions: {
            headerName: "Views",
            name: "View",
            resourceType: "JIRA",
            selectedLayoutKey: "JIRA_SELECTED_LAYOUT",
            fetchParams: {
              portalId: portalData?._id,
            },
          },
        })}
        onClickLayout={handleClickLayout}
      />
    </Box>
  );

  const modalContent = () => {
    if (showCreationLoader) {
      return (
        <Box className="flex justify-center flex-col items-center gap-5 h-full w-full">
          <Text className="font-semibold text-center">
            {t("It may take a few seconds to create ticket and add attachments if any")}
          </Text>
          <CircularProgress />
        </Box>
      );
    }
    return (
      <Box className="flex flex-col p-4 gap-5 w-full h-full overflow-auto">
        <Box className="flex gap-4 font-semibold flex-col ">
          {_.get(ticketHeaderInfo, `${showTicketCreationForm.type}.header`)}
        </Box>
        <Box className="flex overflow-auto flex-col gap-8 w-full h-full">
          {ticketCreationFields.fields.map(
            (data: TicketFormField) =>
              data.heading.allowIn.includes(showTicketCreationForm.type) && (
                <Box key={data.heading.text} className="flex flex-col gap-2 w-full">
                  <Box className="font-semibold">{data.heading.text}</Box>
                  {data.subHeading?.allowIn.includes(showTicketCreationForm.type) && (
                    <Box className="text-sm">{data.subHeading.text}</Box>
                  )}
                  {data.component.allowIn.includes(showTicketCreationForm.type) && (
                    <Box className="flex">{data.component.component}</Box>
                  )}
                </Box>
              ),
          )}
        </Box>
      </Box>
    );
  };

  const getHeaderTitle = () => {
    if (_.get(ticketHeaderInfo, `${showTicketCreationForm.type}`)) {
      return (
        <Box className="flex items-center gap-2">
          <IconComponent
            name={_.get(ticketHeaderInfo, `${showTicketCreationForm.type}.svgName`)}
            width="2rem"
            height="2rem"
          />
          {_.get(ticketHeaderInfo, `${showTicketCreationForm.type}.title`)}
        </Box>
      );
    }
  };

  const footerContent = () => {
    return (
      <ButtonComponent
        disabled={isDisabledSaveButton || !canRaiseSupportTicket}
        onClick={handleSaveBugDetails}
        loading={showCreationLoader}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
      >
        <Text>{t("Save")}</Text>
      </ButtonComponent>
    );
  };

  const handleFilterChange = (args: SelectChangeEvent<string>) => {
    const value = args.target.value;

    if (!value || value === createdByFilter) return;

    searchParams.set("createdBy", value);
    setCreatedByFilter(value);
    setQuery({ action: "getList", createdBy: value, requiresCounts: true });
    setViewTrigger({ queryConditions: { createdBy: value } });

    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const displayTicketsFilter = () => {
    const reportedFilter = getReportedFilter(isCurrentUserWhiteListed, t);
    return (
      <Box className="flex px-8 py-4 items-center gap-4 w-full">
        <Text className="text-base">{t("Display tickets")}</Text>
        <Select value={createdByFilter} name="createdBy" onChange={handleFilterChange} sx={{ height: "2.1875rem" }}>
          {reportedFilter.map((val) => (
            <MenuItem key={val.value} value={val.value}>
              {val.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  return (
    <Box key="jiraTickets" className="flex flex-col w-full h-full">
      {createdByFilter && displayTicketsFilter()}
      {columnDefinitions.length > 0 && getJiraGrid()}
      <ModalStateDialogComponent
        height="80%"
        width="70%"
        isVisible={showTicketCreationForm.status}
        onCancel={closeTicketCreationModal}
        footerContent={footerContent()}
        modelContent={modalContent()}
        headerTitle={getHeaderTitle()}
      />
      {/* Mounted only while open: SlaExportModal seeds its pickers in state initialisers, so
          unmounting on close IS how the next opening gets a fresh month and portal. */}
      {showSlaExportModal && (
        <SlaExportModal
          isVisible
          isWhiteListedUser={Boolean(isWhiteListedUser)}
          portals={slaReportPortals}
          exporting={isExporting}
          onCancel={() => setShowSlaExportModal(false)}
          onExport={handleExportSlaReport}
        />
      )}
      <ChangeTicketTypeModal
        isVisible={showChangeTypeModal}
        // A grid row only reaches here once the user selects it, so it always carries `_id`; the row
        // type leaves it optional because SlickGrid's own row base does.
        ticket={selectedTicket as (JiraTicketRow & { _id: string }) | null}
        onCancel={() => setShowChangeTypeModal(false)}
        onConfirm={handleChangeTypeConfirm}
        loading={changeTypeLoading}
      />
    </Box>
  );
};

export default JiraTickets;
